/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { authApi } from '../api/auth';
import { useAuth } from './AuthContext';
import { queryClient, ACTIVE_EMPRESA_STORAGE_KEY } from '../queryClient';

const EMPRESAS_CACHE_TTL_MS = 30_000;
const EMPRESAS_DEFAULT_CACHE_KEY = '__default__';

export interface EmpresaAcessoItem {
  id: string;
  codigo: string;
  nome: string;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  matriz: boolean;
  ativo: boolean;
}

interface EmpresaAtivaContextType {
  empresas: EmpresaAcessoItem[];
  activeEmpresaId: string | null;
  setActiveEmpresaId: (empresaId: string) => Promise<void>;
  isLoadingEmpresas: boolean;
  isEmpresaContextReady: boolean;
}

const EmpresaAtivaContext = createContext<EmpresaAtivaContextType | null>(null);

// Sincroniza AMBOS os storages: sessionStorage (persistência de sessão)
// e localStorage (lido pelo queryKeyHashFn para isolar cache por empresa).
const saveEmpresaStorage = (id: string) => {
  sessionStorage.setItem(ACTIVE_EMPRESA_STORAGE_KEY, id);
  localStorage.setItem(ACTIVE_EMPRESA_STORAGE_KEY, id);
};

const clearEmpresaStorage = () => {
  sessionStorage.removeItem(ACTIVE_EMPRESA_STORAGE_KEY);
  localStorage.removeItem(ACTIVE_EMPRESA_STORAGE_KEY);
};

export const EmpresaAtivaProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [empresas, setEmpresas] = useState<EmpresaAcessoItem[]>([]);
  const [activeEmpresaId, setActiveEmpresaIdState] = useState<string | null>(null);
  const [isLoadingEmpresas, setIsLoadingEmpresas] = useState(false);
  const [isEmpresaContextReady, setIsEmpresaContextReady] = useState(false);
  const empresasCacheRef = useRef<Record<string, { ts: number; data: { active_empresa_id: string | null; empresas: EmpresaAcessoItem[] } }>>({});

  const getCacheKey = (empresaId?: string) => empresaId || EMPRESAS_DEFAULT_CACHE_KEY;

  const getCachedEmpresas = useCallback((empresaId?: string) => {
    const key = getCacheKey(empresaId);
    const entry = empresasCacheRef.current[key];
    if (!entry) return null;
    if (Date.now() - entry.ts > EMPRESAS_CACHE_TTL_MS) return null;
    return entry.data;
  }, []);

  const syncEmpresas = useCallback(async (requestedEmpresaId?: string) => {
    if (!user) {
      setEmpresas([]);
      setActiveEmpresaIdState(null);
      clearEmpresaStorage();
      setIsEmpresaContextReady(true);
      return;
    }

    const cached = getCachedEmpresas(requestedEmpresaId);
    if (cached) {
      const fetchedEmpresas = cached.empresas || [];
      const resolvedActiveId =
        requestedEmpresaId ||
        cached.active_empresa_id ||
        fetchedEmpresas.find((e) => e.matriz)?.id ||
        fetchedEmpresas[0]?.id ||
        null;
      setEmpresas(fetchedEmpresas);
      setActiveEmpresaIdState(resolvedActiveId);
      if (resolvedActiveId) {
        saveEmpresaStorage(resolvedActiveId);
      } else {
        clearEmpresaStorage();
      }
      setIsEmpresaContextReady(true);
      return;
    }

    setIsLoadingEmpresas((prev) => prev || !requestedEmpresaId);
    try {
      const data = await authApi.empresasAcesso(requestedEmpresaId);
      empresasCacheRef.current = {
        ...empresasCacheRef.current,
        [getCacheKey(requestedEmpresaId)]: { ts: Date.now(), data },
      };
      const fetchedEmpresas = data.empresas || [];
      const resolvedActiveId =
        requestedEmpresaId ||
        data.active_empresa_id ||
        fetchedEmpresas.find((e) => e.matriz)?.id ||
        fetchedEmpresas[0]?.id ||
        null;
      setEmpresas(fetchedEmpresas);
      setActiveEmpresaIdState(resolvedActiveId);
      if (resolvedActiveId) {
        saveEmpresaStorage(resolvedActiveId);
      } else {
        clearEmpresaStorage();
      }
    } catch (error: unknown) {
      // Fallback seguro para ambientes onde o endpoint ainda não foi publicado.
      const status = typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
      if (status === 404) {
        setEmpresas([]);
        setActiveEmpresaIdState(null);
        clearEmpresaStorage();
        return;
      }
      throw error;
    } finally {
      setIsLoadingEmpresas(false);
      setIsEmpresaContextReady(true);
    }
  }, [getCachedEmpresas, user]);

  useEffect(() => {
    // Aguarda o auth terminar de carregar antes de sincronizar empresa.
    // Se disparar com user=null ainda em loading, removeItem apagaria a filial salva
    // e no segundo disparo (user carregado) não haveria stored → volta para matriz.
    if (isAuthLoading) return;
    const stored = sessionStorage.getItem(ACTIVE_EMPRESA_STORAGE_KEY) || undefined;
    void syncEmpresas(stored).catch(() => {
      // Evita promessa rejeitada sem tratamento no bootstrap do app.
      setEmpresas([]);
      setActiveEmpresaIdState(null);
      clearEmpresaStorage();
    });
  }, [syncEmpresas, user?.id, isAuthLoading]);

  const setActiveEmpresaId = useCallback(async (empresaId: string) => {
    if (!empresaId || empresaId === activeEmpresaId) return;

    // Troca otimista: muda estado e ambos os storages imediatamente.
    // localStorage é lido pelo queryKeyHashFn — deve ser atualizado ANTES das queries refetcharem.
    setActiveEmpresaIdState(empresaId);
    saveEmpresaStorage(empresaId);

    // Invalida todo o cache do React Query para que as queries recarreguem
    // com a nova empresa ativa. Sem isso, dados da empresa anterior ficam visíveis
    // até o gcTime expirar (padrão: 5 min).
    void queryClient.invalidateQueries();

    // Sincroniza com backend em background sem bloquear a troca visual.
    void syncEmpresas(empresaId).catch(() => {
      // Mantém estado otimista; fallback será corrigido no próximo bootstrap/sync.
    });
  }, [activeEmpresaId, syncEmpresas]);

  const value = useMemo<EmpresaAtivaContextType>(
    () => ({
      empresas,
      activeEmpresaId,
      setActiveEmpresaId,
      isLoadingEmpresas,
      isEmpresaContextReady,
    }),
    [empresas, activeEmpresaId, isLoadingEmpresas, setActiveEmpresaId, isEmpresaContextReady],
  );

  return <EmpresaAtivaContext.Provider value={value}>{children}</EmpresaAtivaContext.Provider>;
};

export const useEmpresaAtiva = () => {
  const context = useContext(EmpresaAtivaContext);
  if (!context) throw new Error('useEmpresaAtiva must be used within EmpresaAtivaProvider');
  return context;
};
